import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';

import Button from '@/components/elements/Button/BaseButton';
import Fingerprint from '@/components/elements/Fingerprint';
import AutoChargeSelect from '@/components/layouts/Modal/BetPanel/Partials/ConfirmBox/AutoCharge';
import Cc3D2AuthModal from '@/components/layouts/Modal/BetPanel/Partials/ConfirmBox/AutoCharge/Cc3D2Auth';
import ErrorModal from '@/components/layouts/Modal/Error';
import { QuickChargeStep } from '@/components/layouts/Modal/Order/QuickCharge';
import { TOKUSHOU_TERMS_URL } from '@/config/constants/terms';
import { useUserContext } from '@/contexts/user';
import {
  CreditCard3d2AuthResponse,
  CreditCard3dAuthStatus,
  CreditCardMemberStatus,
  useChargeToretaFromCreditV2WithValidateOrderMutation,
  useChargeToretaFromWalletWithValidateOrderMutation,
  useGetCreditCardStatusQuery,
} from '@/lib/apollo/types/gen/graphql';
import { sendToretaChargeEvent } from '@/lib/dmm/trackingTag';
import { ChargeToretaLabel } from '@/lib/dmm/trackingTag/type';
import { OrderStep } from '@/modelTypes';

import ReadyButton from './ReadyButton';
import {
  AutoChargeSelectWrapper,
  BeforeBetConfirmText,
  ButtonWrapper,
  ConfirmArea,
  ConfirmText,
  Detail,
  LinkText,
  PaymentMethodLink,
  Title,
} from './style';

const AUTO_CHARGE_BY_CREDIT_MINIMUM_AMOUNT = 1000;

interface ConfirmBoxProps {
  orderStep: OrderStep;
  setOrderStep: React.Dispatch<React.SetStateAction<OrderStep>>;
  totalBetCount: number;
  totalPrice: number;
  purchaseTickets: (userDevice: string) => void;
  disabled: boolean;
  isProcessing: boolean;
  isAutoChargeByCredit: boolean;
  setIsAutoChargeByCredit: React.Dispatch<React.SetStateAction<boolean>>;
}

const ConfirmBox: React.FC<ConfirmBoxProps> = ({
  orderStep,
  setOrderStep,
  totalBetCount,
  totalPrice,
  purchaseTickets,
  disabled,
  isProcessing,
  isAutoChargeByCredit,
  setIsAutoChargeByCredit,
}) => {
  const { user } = useUserContext();

  // その場でチャージ
  /// 不足金額
  const insufficientBalance = useMemo(() => {
    if (!user.wallet) {
      return 0;
    }
    return totalPrice - user.wallet.toreta.balance;
  }, [totalPrice, user.wallet?.toreta.balance]);

  // その場でチャージ設定（入金/払戻金 残高）
  const [isAutoChargeByCash, setIsAutoChargeByCash] = useState<boolean>(false);

  const [isAutoChargeProcessing, setIsAutoChargeProcessing] =
    useState<boolean>(false);
  const [userDevice, setUserDevice] = useState<string>('');

  const [quickChargeStep, setQuickChargeStep] = useState<QuickChargeStep>(
    QuickChargeStep.Standby,
  );

  // 入金/清算金残高 と クレジットカードでのチャージは排他
  useEffect(() => {
    if (isAutoChargeByCash) {
      setIsAutoChargeByCredit(false);
    }
  }, [isAutoChargeByCash]);
  useEffect(() => {
    if (isAutoChargeByCredit) {
      setIsAutoChargeByCash(false);
    }
  }, [isAutoChargeByCredit]);

  const isAutoChargeEnabled = useMemo(() => {
    return isAutoChargeByCash || isAutoChargeByCredit;
  }, [isAutoChargeByCash, isAutoChargeByCredit]);

  // オートチャージ額（クレカ決済の場合は最低チャージ金額があるため）
  const autoChargeAmount = useMemo(() => {
    if (
      isAutoChargeByCredit &&
      insufficientBalance < AUTO_CHARGE_BY_CREDIT_MINIMUM_AMOUNT
    ) {
      return AUTO_CHARGE_BY_CREDIT_MINIMUM_AMOUNT;
    }
    return insufficientBalance;
  }, [isAutoChargeByCredit, insufficientBalance]);

  // 不足金額がウォレット残高を上回ったら、ウォレットからのその場でチャージをOFFにする
  useEffect(() => {
    if (user.wallet && user.wallet.balance < insufficientBalance) {
      setIsAutoChargeByCash(false);
    }
  }, [insufficientBalance]);

  // クレジットカード toreta+チャージMutation
  const [chargeToretaFromCreditV2Mutation] =
    useChargeToretaFromCreditV2WithValidateOrderMutation();
  const [auth2Response, setAuth2Response] =
    useState<CreditCard3d2AuthResponse | null>(null);

  // 入金/清算金残高 toreta+チャージMutation
  const [chargeToretaFromWalletMutation, chargeToretaFromWalletMutationResult] =
    useChargeToretaFromWalletWithValidateOrderMutation();

  const creditCardStatus = useGetCreditCardStatusQuery({
    pollInterval: 10 * 1000,
  });

  // 3Dセキュア認証モーダル
  const [isOpenCc3DModal, setIsOpenCc3DModal] = useState<boolean>(false);

  const [isOpenErrorModal, setIsOpenErrorModal] = useState<boolean>(false);

  // 確定ボタン押下後 その場でチャージ実行/購入 のハンドリング
  const handleConfirm = () => {
    if (isProcessing || isAutoChargeProcessing || disabled) {
      return;
    }

    // その場でチャージ有効
    if (isAutoChargeEnabled) {
      setIsAutoChargeProcessing(true);
      setQuickChargeStep(QuickChargeStep.InProgress);
      // クレジットカード その場でチャージ
      // 3Dセキュアモーダルを表示し、認証完了後はuseEffectで購入実行
      if (isAutoChargeByCredit) {
        chargeToretaFromCreditV2Mutation({
          variables: {
            amount: autoChargeAmount,
            orderAmount: totalPrice,
          },
          context: {
            headers: {
              'X-User-Device': userDevice,
            },
          },
        })
          .then(res => {
            if (
              res.data &&
              res.data.chargeToretaFromCreditV2WithValidateOrder.result ===
                CreditCard3dAuthStatus.Approve
            ) {
              // 3Dセキュアモーダルを表示
              setAuth2Response(
                res.data.chargeToretaFromCreditV2WithValidateOrder,
              );
              setIsOpenCc3DModal(true);
              return Promise.resolve();
            }
            return Promise.reject();
          })
          .catch(() => {
            setIsAutoChargeProcessing(false);
            setIsOpenErrorModal(true);
          });
        return;
      }
      // ウォレット その場でチャージ
      // 生産可能額→toreta+ チャージ後に購入実行
      if (isAutoChargeByCash) {
        chargeToretaFromWalletMutation({
          variables: {
            amount: insufficientBalance,
            orderAmount: totalPrice,
          },
        })
          .then(res => {
            if (res.data) {
              setIsAutoChargeProcessing(false);
              sendToretaChargeEvent(ChargeToretaLabel.PreChargeFromWallet);
              purchaseTickets(userDevice);
              return Promise.resolve();
            }
            return Promise.reject();
          })
          .catch(() => {
            setIsAutoChargeProcessing(false);
            setIsOpenErrorModal(true);
          });
        return;
      }
    }

    // その場でチャージ無効 購入実行
    purchaseTickets(userDevice);
  };

  // 3Dセキュア認証モーダル閉じられたときのハンドリング（中断）
  const handleCloseCc3DAuthModal = useCallback(() => {
    setIsAutoChargeProcessing(false);
    setIsOpenCc3DModal(false);
  }, []);
  // その場でチャージ成功後に購入
  const handleChargeToretaFromCreditSuccess = () => {
    setIsOpenCc3DModal(false);
    setIsAutoChargeProcessing(false);
    setQuickChargeStep(QuickChargeStep.Succeed);
    sendToretaChargeEvent(ChargeToretaLabel.PreChargeFromCredit);
    purchaseTickets(userDevice);
  };
  // その場でチャージ失敗時はReady状態に復元する
  const handleChargeToretaFromCreditFailure = useCallback(() => {
    setIsAutoChargeProcessing(false);
    setQuickChargeStep(QuickChargeStep.Failed);
    setIsAutoChargeByCredit(false);
    setOrderStep(OrderStep.Ready);
  }, []);

  const handleChargeToretaFromCreditTimeout = useCallback(() => {
    setIsAutoChargeProcessing(false);
    setQuickChargeStep(QuickChargeStep.Timeout);
    setIsAutoChargeByCredit(false);
    setOrderStep(OrderStep.Ready);
  }, []);

  // 自動チャージ成功後に投票が失敗したとき自動チャージを解除する処理
  useEffect(() => {
    if (
      isAutoChargeEnabled &&
      orderStep === OrderStep.Ready &&
      insufficientBalance <= 0
    ) {
      setIsAutoChargeByCredit(false);
      setIsAutoChargeByCash(false);
    }
  }, [isAutoChargeEnabled, orderStep, insufficientBalance]);

  return (
    <ConfirmArea>
      {orderStep === OrderStep.Confirm && (
        <ConfirmText>
          <p data-testid="confirm-text">投票確認</p>
          投票パネルの内容で投票します。
          <br />
          投票した内容は取り消しできません。
        </ConfirmText>
      )}
      <Detail>
        <Title>組数</Title>
        <span data-testid="total-bet-count">
          {totalBetCount.toLocaleString()}組
        </span>
      </Detail>
      <Detail>
        <Title>投票額</Title>
        <span data-testid="total-bet-price">
          {totalPrice.toLocaleString()}円
        </span>
      </Detail>
      {orderStep === OrderStep.Confirm &&
        insufficientBalance > 0 &&
        isAutoChargeEnabled && (
          <Detail>
            <Title data-testid="charge-type-name">
              toreta+ チャージ
              <br />
              {isAutoChargeByCash && '(入金/払戻金 残高)'}
              {isAutoChargeByCredit && '(クレジットカード)'}
            </Title>
            <span data-testid="auto-charge-amount">
              {autoChargeAmount.toLocaleString()}円分
            </span>
          </Detail>
        )}

      {/* その場でチャージ選択 */}
      {orderStep === OrderStep.Ready && insufficientBalance > 0 && (
        <AutoChargeSelectWrapper>
          <AutoChargeSelect
            insufficientBalance={insufficientBalance}
            isAutoChargeByCash={isAutoChargeByCash}
            setIsAutoChargeByCash={setIsAutoChargeByCash}
            isAutoChargeByCredit={isAutoChargeByCredit}
            setIsAutoChargeByCredit={setIsAutoChargeByCredit}
            creditCardStatus={
              (creditCardStatus.data?.creditCardStatus
                .memberStatus as CreditCardMemberStatus) ||
              CreditCardMemberStatus.Unknown
            }
          />
        </AutoChargeSelectWrapper>
      )}
      <>
        {auth2Response && (
          <Cc3D2AuthModal
            isOpen={isOpenCc3DModal}
            handleClose={handleCloseCc3DAuthModal}
            authResponse={auth2Response}
            onSuccess={handleChargeToretaFromCreditSuccess}
            onFailure={handleChargeToretaFromCreditFailure}
            onTimeout={handleChargeToretaFromCreditTimeout}
            chargeStep={quickChargeStep}
          />
        )}
      </>
      {orderStep === OrderStep.Ready && (
        <>
          <ReadyButton
            disabled={
              disabled ||
              (insufficientBalance > 0 &&
                !isAutoChargeByCash &&
                !isAutoChargeByCredit)
            }
            setOrderStep={setOrderStep}
          />
        </>
      )}
      {orderStep === OrderStep.Confirm && (
        <>
          <BeforeBetConfirmText>
            <p>
              投票前にご確認ください。
              <br />
              <LinkText>
                <a href={`${TOKUSHOU_TERMS_URL}#payment`} target="_blank">
                  代金の支払い時期
                </a>
                、
                <a href={`${TOKUSHOU_TERMS_URL}#available`} target="_blank">
                  商品等の引き渡し時期
                </a>
                、
                <a href={`${TOKUSHOU_TERMS_URL}#return`} target="_blank">
                  投票の変更・取り消し
                </a>
              </LinkText>
              車券の投票はtoreta+ のみで行えます。
              <br />
              {isAutoChargeByCredit && (
                <>
                  toreta+ は決済完了後即時に利用できます。
                  <br />
                  toreta+
                  はいかなる場合も返金または換金することはできません。予めご了承ください。
                  <PaymentMethodLink
                    href={`${TOKUSHOU_TERMS_URL}#payment_method`}
                    target="_blank"
                  >
                    toreta+ の支払い方法について
                  </PaymentMethodLink>
                </>
              )}
            </p>
          </BeforeBetConfirmText>
          <ButtonWrapper>
            <Button
              size="m"
              bgColor="primary"
              fontWeight="bold"
              height="56px"
              radius="l"
              isLoading={isProcessing || isAutoChargeProcessing}
              disabled={isProcessing || isAutoChargeProcessing || disabled}
              onClick={() => handleConfirm()}
            >
              確定する
            </Button>
            <Button
              outline
              size="m"
              bgColor="darkGray"
              fontWeight="bold"
              height="56px"
              radius="l"
              disabled={isProcessing || isAutoChargeProcessing || disabled}
              onClick={() => setOrderStep(OrderStep.Ready)}
            >
              キャンセル
            </Button>
          </ButtonWrapper>
        </>
      )}
      <ErrorModal
        isOpen={isOpenErrorModal}
        handleClose={() => setIsOpenErrorModal(false)}
        apolloError={chargeToretaFromWalletMutationResult.error}
      />
      <Fingerprint setState={setUserDevice} />
    </ConfirmArea>
  );
};

export default ConfirmBox;
